// Seule grille tarifaire du front React. Trois écrans la montrent :
//   - /pricing et la landing (variant "page" : bascule #billingToggle, carte
//     Entreprise quand `data.enterprise` est fourni) ;
//   - la diapositive d'offre du carrousel de bienvenue (variant "modal",
//     listes raccourcies par le serveur).
// Données : ReactProps::PricingPlansProps#react_pricing_plans (tout le texte
// est calculé par Rails). La bascule mensuel / annuel, limitée à la grille,
// est un état local.
//
// Un blanc suivi d'un texte reste dans le même nœud de texte que lui :
// séparés, ils ne sont pas crénés pareil et le texte se décale d'une fraction
// de pixel. Les blancs entre deux blocs ({' '}) ne se voient pas, mais
// séparent les mots du texte quand la grille est masquée (diapositive d'offre
// du carrousel non affichée).
//
// Les formulaires restent des POST natifs vers /billing/checkout (redirection
// Stripe, rechargement complet). Au premier envoi, la grille passe en
// attente : bouton envoyé en .is-busy, les autres désactivés, un double clic
// n'ouvre pas deux sessions.
// Retour arrière depuis Stripe (page restaurée du cache) : la grille repart
// active.
import { Fragment, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import AppLink from '@/components/ui/AppLink'
import { useShared } from '@/hooks/useShared'
import type { PricingCard, PricingPlansData } from './types'

const HIDDEN: CSSProperties = { display: 'none' }
const PERIODS = ['monthly', 'yearly'] as const

type Period = (typeof PERIODS)[number]

// Note de remise d'un intervalle : l'émoji de la campagne, ou la fusée du
// prix de lancement.
const promoNote = (note: string, icon: string | null) => (
  <span className="pricing-promo-note">
    {icon ? <span aria-hidden="true">{icon}</span> : <i className="fas fa-rocket" aria-hidden="true"></i>}
    {` ${note}`}
  </span>
)

export default function PricingPlans({ data }: { data: PricingPlansData }) {
  const { csrfToken } = useShared()
  // Coché au rendu : annuel affiché d'emblée (applyBilling(toggle.checked)).
  const [yearly, setYearly] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const busyRef = useRef<string | null>(null)

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      busyRef.current = null
      setBusy(null)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const show = (period: Period): CSSProperties | undefined => ((period === 'yearly') === yearly ? undefined : HIDDEN)

  const onSubmit = (key: string) => (event: FormEvent<HTMLFormElement>) => {
    if (busyRef.current !== null) {
      event.preventDefault()
      return
    }
    busyRef.current = key
    setBusy(key)
  }

  const checkoutForm = (card: PricingCard, period: Period) => {
    if (card.cta.kind !== 'checkout') return null
    const key = `${card.key}-${period}`
    const target = card.cta[period]
    const isSubmitter = busy === key
    return (
      <div className={`billing-${period}`} style={show(period)}>
        <form
          className="w-100"
          action={data.checkoutPath}
          acceptCharset="UTF-8"
          method="post"
          data-busy={isSubmitter ? 'true' : undefined}
          onSubmit={onSubmit(key)}
        >
          <input type="hidden" name="authenticity_token" value={csrfToken} autoComplete="off" />
          <input value={target.priceId ?? undefined} autoComplete="off" type="hidden" name="price_id" id="price_id" />
          <input value={target.planType} autoComplete="off" type="hidden" name="plan_type" id="plan_type" />
          <input
            type="submit"
            name="commit"
            value={card.cta.label}
            className={`${card.cta.btnClass}${isSubmitter ? ' is-busy' : ''}`}
            aria-busy={isSubmitter ? 'true' : undefined}
            disabled={busy !== null && !isSubmitter}
          />
        </form>
      </div>
    )
  }

  return (
    <div className={`pricing-plans pricing-plans--${data.variant}`} id={data.gridId}>
      <div className="pricing-toggle-wrap">
        <span className={`pricing-toggle-label${yearly ? '' : ' active'}`}>{data.monthlyLabel}</span>{' '}
        <label className="pricing-toggle-switch">
          <input
            type="checkbox"
            className="js-billing-toggle"
            id={data.variant === 'page' ? 'billingToggle' : undefined}
            checked={yearly}
            onChange={(e) => setYearly(e.target.checked)}
          />
          <span className="pricing-toggle-slider"></span>
        </label>{' '}
        <span className={`pricing-toggle-label${yearly ? ' active' : ''}`}>
          {`${data.yearlyLabel} `}
          <span className="pricing-save-badge">{data.toggleSave}</span>
        </span>
      </div>{' '}

      <div className="pricing-cards-grid">
        {data.cards.map((card) => {
          const price = card.price
          const cta = card.cta
          const promoBadge = card.promoBadge?.[yearly ? 'yearly' : 'monthly']
          return (
            <Fragment key={card.key}>
              <div
                className={['pricing-card-new', card.popular && 'pricing-card-popular', card.current && 'pricing-card-current'].filter(Boolean).join(' ')}
              >
                {promoBadge && <><div className="pricing-promo-badge">{promoBadge}</div>{' '}</>}
                {card.popular && <><div className="pricing-popular-badge">{card.popularLabel}</div>{' '}</>}

                <div className="pricing-card-header">
                  <div className="pricing-plan-name">{card.name}</div>{' '}
                  <div className="pricing-plan-tagline">{card.tagline}</div>
                </div>{' '}

                <div className="pricing-price-block">
                  {price.kind === 'free' && (
                    <>
                      <div className="pricing-price-main">
                        <span className="pricing-amount">{price.amount}</span>
                      </div>{' '}
                      <div className="pricing-price-sub">{price.sub}</div>
                    </>
                  )}
                  {price.kind === 'promo' && (
                    <>
                      {/* Un intervalle hors campagne (sans prix barré) s'affiche comme hors promo. */}
                      {PERIODS.map((period) => (
                        <Fragment key={period}>
                          <div className={`pricing-price-main billing-${period}`} style={show(period)}>
                            {price[period].original ? (
                              <>
                                <span className="pricing-amount-original"><s>{price[period].original}</s></span>{' '}
                                <span className="pricing-amount pricing-amount-promo">{price[period].amount}</span>{' '}
                              </>
                            ) : (
                              <><span className="pricing-amount">{price[period].amount}</span>{' '}</>
                            )}
                            <span className="pricing-period">{price.period}</span>
                          </div>{' '}
                        </Fragment>
                      ))}
                      <div className="pricing-price-sub billing-monthly" style={show('monthly')}>
                        {price.monthlyNote ? promoNote(price.monthlyNote, price.noteIcon) : price.monthlySub}
                      </div>{' '}
                      <div className="pricing-price-sub billing-yearly" style={show('yearly')}>
                        {price.yearlyNote ? (
                          <>
                            {promoNote(price.yearlyNote, price.noteIcon)}{' '}
                            <span className="pricing-save-inline">{price.yearlySaving}</span>
                          </>
                        ) : (
                          <>
                            {`${price.yearlySub} — `}
                            <span className="pricing-save-inline">{price.yearlySaving}</span>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  {price.kind === 'regular' && (
                    <>
                      {PERIODS.map((period) => (
                        <Fragment key={period}>
                          <div className={`pricing-price-main billing-${period}`} style={show(period)}>
                            <span className="pricing-amount">{price[period].amount}</span>{' '}
                            <span className="pricing-period">{price.period}</span>
                          </div>{' '}
                        </Fragment>
                      ))}
                      <div className="pricing-price-sub billing-monthly" style={show('monthly')}>
                        {price.monthlySub}
                      </div>{' '}
                      <div className="pricing-price-sub billing-yearly" style={show('yearly')}>
                        {`${price.yearlySub} — `}
                        <span className="pricing-save-inline">{price.yearlySaving}</span>
                      </div>
                    </>
                  )}
                </div>{' '}

                {card.trialChip && (
                  <><div className="pricing-trial-chip"><i className="fas fa-gift" aria-hidden="true"></i>{` ${card.trialChip}`}</div>{' '}</>
                )}

                <div className="pricing-cta-wrap">
                  {cta.kind === 'current' && (
                    <div className="pricing-btn-current"><i className="fas fa-check"></i>{` ${cta.label}`}</div>
                  )}
                  {cta.kind === 'signup' && (
                    <AppLink className="pricing-btn pricing-btn-outline" href={cta.href}>
                      {`${cta.label} `}<i className="fas fa-arrow-right"></i>
                    </AppLink>
                  )}
                  {cta.kind === 'checkout' && (
                    <>
                      {checkoutForm(card, 'monthly')}{' '}
                      {checkoutForm(card, 'yearly')}{' '}
                    </>
                  )}
                  {cta.kind === 'visitor' &&
                    PERIODS.map((period) => (
                      <Fragment key={period}>
                        <div className={`billing-${period}`} style={show(period)}>
                          <AppLink className={cta.btnClass} href={cta.href}>
                            {cta.label}
                          </AppLink>
                        </div>{' '}
                      </Fragment>
                    ))}
                  {(cta.kind === 'checkout' || cta.kind === 'visitor') && cta.trialNote && (
                    <p className="pricing-trial-note">{cta.trialNote}</p>
                  )}
                </div>{' '}

                <ul className="pricing-features-list">
                  {card.features.map((feature, i) => (
                    <Fragment key={i}>
                      {feature.kind === 'highlight' ? (
                        <li className="pricing-feature-highlight"><i className="fas fa-check pricing-check"></i> <strong>{feature.text}</strong></li>
                      ) : feature.kind === 'includes' ? (
                        <li className="pricing-feature-includes"><i className="fas fa-layer-group me-1"></i> <em>{feature.text}</em></li>
                      ) : (
                        <li><i className="fas fa-check pricing-check"></i>{` ${feature.text}`}</li>
                      )}{' '}
                    </Fragment>
                  ))}
                  {card.more && (
                    <li className="pricing-feature-more">
                      {/* <a> simple : rechargement complet. */}
                      <a href={card.more.href}>{card.more.label}</a>
                    </li>
                  )}
                </ul>
              </div>{' '}
            </Fragment>
          )
        })}

        {data.enterprise && (
          <div className="pricing-card-new pricing-card-enterprise">
            <div className="pricing-card-header">
              <div className="pricing-plan-name">{data.enterprise.name}</div>{' '}
              <div className="pricing-plan-tagline">{data.enterprise.tagline}</div>
            </div>{' '}
            <div className="pricing-price-block">
              <div className="pricing-price-main">
                <span className="pricing-amount">{data.enterprise.price}</span>
              </div>{' '}
              <div className="pricing-price-sub">{data.enterprise.sub}</div>
            </div>{' '}
            <div className="pricing-cta-wrap">
              <a className="pricing-btn pricing-btn-outline" href="mailto:contact@northvane.app">
                {`${data.enterprise.cta} `}<i className="fas fa-envelope"></i>
              </a>
            </div>{' '}
            <ul className="pricing-features-list">
              {data.enterprise.features.map((f) => (
                <Fragment key={f}>
                  <li><i className="fas fa-check pricing-check"></i>{` ${f}`}</li>{' '}
                </Fragment>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
